import {AoaSheet, AxisMapped, ReportSheet, YAxisID} from "interfaces/report";
import { chain, cloneDeep } from "lodash";
import { Line } from "react-chartjs-2";
import {convertStringToNumber, getTextColor} from "utils/string";
import numeral from "numeral";

interface Props {
  sheet: ReportSheet<AxisMapped>;
}

export const TrendLineChart = ({ sheet }: Props) => {
  sheet.yAxis = sheet.yAxis.filter((item) => !item.hide && item.active);

  const mergedYValue = chain(sheet.yAxis)
    .map("value")
    .uniq()
    .join(" | ")
    .value();

  const data = {
    labels: sheet.xAxis.map((item) => item.value),
    datasets: sheet.yAxis
      .filter((item) => !item.hide && item.active)
      .map((item) => {
        const color = item.color;
        const rawData = sheet.data
          .find((items) => items[0] === item.value)
          ?.slice(1).map((d) => convertStringToNumber(d)) as AoaSheet;

        // Filter out invalid values (NaN, null, undefined, Infinity)
        const data = (rawData || []).map((val) => {
          const num = Number(val);
          return !isFinite(num) ? 0 : num;
        });

        // Check if we have valid data for trendline (at least 2 different values)
        const hasValidTrendlineData = data.length >= 2 &&
          data.some((val, idx, arr) => arr.some((v, i) => i !== idx && v !== val));

        return {
          label: item.value,
          borderColor: color,
          backgroundColor: color,
          data,
          // Only add trendline if we have valid data
          ...(hasValidTrendlineData && {
            trendlineLinear: {
              style: color,
              width: 5,
              colorMin: color + "40",
              colorMax: color + "80",
            },
          }),
        };
      }),
  };

  return (
    <Line
      options={cloneDeep({
        responsive: true,
        aspectRatio: 5 / 3,
        layout: {
          padding: {
            left: 0,
            right: 40,
          },
        },
        elements: {
          line: {
            fill: false,
            tension: 0.4,
          },
        },
        scales: {
          y: {
            ticks: {
              // Include a dollar sign in the ticks
              callback: function(value, index, ticks) {
                const unit = sheet.yAxis.find((axis) => axis.yAxisID === YAxisID.Y)?.unit;
                if (unit === '%') {
                  return numeral(value).format('0.0%');
                } else {
                  return numeral(value).format(`${unit}0.0a`);
                }
              }
            },
            title: {
              display: false,
              text: mergedYValue,
            },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label(tooltipItem) {
                const value = tooltipItem.parsed.y;
                const unit = sheet.yAxis.find((axis) => axis.yAxisID === YAxisID.Y)?.unit;

                let formatted = '';
                if (unit === '%') {
                  formatted = numeral(value).format('0.0%');
                } else if (unit === '$') {
                  formatted = numeral(value).format('$0,0.00');
                } else {
                  formatted = value.toLocaleString("en-AU", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  });
                }

                return `${tooltipItem.dataset.label}: ${formatted}`;
              },
            },
          },
          datalabels: {
            backgroundColor: (context) => {
              return context.dataset.backgroundColor as string;
            },
            color: (context) => {
              return getTextColor(context.dataset.backgroundColor as string);
            },
            borderRadius: 20,
            font: {
              weight: "bold",
            },
            formatter: (value) => {
              const unit = sheet.yAxis.find((axis) => axis.yAxisID === YAxisID.Y)?.unit;
              if (unit === '%') {
                return numeral(value).format('0.0%');
              } else if (unit === '$') {
                return numeral(value).format('$0.00a');
              } else {
                return numeral(value).format('0.00a');
              }
            },
            padding: 6,
          },

          legend: {
            display: false,
          },
        },
      })}
      data={data}
    />
  );
};
